import { DEFAULT_RECEIPT_MS, DEFAULT_SIGNATURE_MS } from '../constants/constants';
import { isMocked } from '../utils/isMocked';
import { orchestrateTx } from '../utils/orchestrate-tx';
import * as Sentry from '@sentry/react';
import { MOCK_ROOM_ID } from 'e2e-tests/mocks/mock-rooms';
import {
  approveHandlerMock,
  createRoomMock,
  getAllowanceMock,
  joinRoomMock,
} from 'tests/mocks/e2e/store/start-game-mock';
import { PublicClient, WalletClient } from 'viem';
import { create } from 'zustand';

import { useUIStore } from 'shared/store/uiStore';
import { WriteError } from 'shared/types/app-types';
import {
  approveHandler,
  createRoom,
  getAllowance,
  joinRoom,
} from 'shared/viem-functions/start-game';
import { handleViemError } from 'shared/viem-functions/utils';

let fullNumber = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff');

export interface State {
  currentAllowance: number;
  txHash: `0x${string}` | null;
  isFetching: boolean;
  isApproving: boolean;
  isStarting: boolean;
  isJoining: boolean;
  error: string | null;
}

export interface Actions {
  fetchCurrentAllowance: (
    ownerAddress: `0x${string}`,
    currency: { address: `0x${string}`; decimals: number },
    provider: PublicClient,
  ) => Promise<void>;
  approveAllowance: (params: {
    amount: number;
    currency: { address: `0x${string}`; decimals: number };
    userAddress: `0x${string}`;
    walletClient: WalletClient;
    publicClient: PublicClient;
    fullAllowance: boolean;
    joinGame: boolean;
  }) => Promise<void>;
  startGame: (params: {
    wins: number;
    currency: `0x${string}`;
    betAmount: bigint;
    playerB: `0x${string}`;
    referrer: `0x${string}`;
    walletClient: WalletClient;
    publicClient: PublicClient;
    userAddress: `0x${string}`;
  }) => Promise<number | undefined>;
  joinRoom: (params: {
    roomId: bigint;
    referrer: `0x${string}`;
    walletClient: WalletClient;
    publicClient: PublicClient;
    userAddress: `0x${string}`;
  }) => Promise<void>;
  handleStoreError: (err: unknown) => void;
}

interface LogWithTopics {
  topics: string[];
}

export const initialState: State = {
  currentAllowance: 0,
  txHash: null,
  isFetching: false,
  isApproving: false,
  isStarting: false,
  isJoining: false,
  error: null,
};

export const useStartGameStore = create<State & Actions>((set, get) => ({
  ...initialState,
  handleStoreError: (err: unknown) => {
    const { shouldShowRpcModal, errorMessage } = handleViemError(err);
    if (shouldShowRpcModal) {
      useUIStore.getState().setRpcErrorModalShow(true);
    }
    set({ error: errorMessage });
  },
  fetchCurrentAllowance: async (ownerAddress, currency, provider) => {
    set({ isFetching: true, error: null });
    try {
      // Guard against missing currency in tests or uninitialized state
      if (!currency || !currency.address || currency.decimals == null) {
        set({ currentAllowance: 0 });
        return;
      }
      if (isMocked) {
        const allowance = await getAllowanceMock();
        set({ currentAllowance: Number(allowance) / 10 ** currency.decimals });
        return;
      }

      const allowance = await getAllowance({
        ownerAddress,
        tokenAddress: currency.address,
        provider,
      });
      set({ currentAllowance: Number(allowance) / 10 ** currency.decimals });
    } catch (err: unknown) {
      console.error('Error fetching allowance:', err);
      const { errorMessage } = handleViemError(err);
      set({ error: errorMessage });
    } finally {
      set({ isFetching: false });
    }
  },
  approveAllowance: async ({
    amount,
    currency,
    userAddress,
    walletClient,
    publicClient,
    fullAllowance,
    joinGame,
  }) => {
    if (!walletClient || !publicClient) return;
    if (!currency || !currency.address || currency.decimals == null) {
      // Missing currency info; nothing to approve
      set({ isApproving: false });
      return;
    }
    set({ isApproving: true, error: null });
    try {
      if (isMocked) {
        set({ txHash: await approveHandlerMock() });
        return;
      }
      if (get().currentAllowance === 0) {
        try {
          await orchestrateTx(
            () =>
              approveHandler({
                amount: fullAllowance
                  ? fullNumber
                  : BigInt(Math.floor(amount) * 10 ** currency.decimals),
                functionName: 'approve',
                currencyAddress: currency.address,
                userAddress,
                walletClient,
                publicClient,
              }),
            (tx) => publicClient.waitForTransactionReceipt({ hash: tx }),
            {
              signatureMs: DEFAULT_SIGNATURE_MS,
              receiptMs: DEFAULT_RECEIPT_MS,
            },
            {
              onTx: (tx) => set({ txHash: tx }),
            },
          );
        } catch (error: unknown) {
          console.error('Error in approve (allowance 0):', error);
          throw error;
        }
      } else if (amount > get().currentAllowance) {
        const amountToIncrease = amount - get().currentAllowance;
        try {
          await orchestrateTx(
            () =>
              approveHandler({
                amount: fullAllowance
                  ? fullNumber
                  : BigInt(Math.floor(amount) * 10 ** currency.decimals),
                functionName: 'increaseAllowance',
                currencyAddress: currency.address,
                userAddress,
                walletClient,
                publicClient,
              }),
            (tx) => publicClient.waitForTransactionReceipt({ hash: tx }),
            {
              signatureMs: DEFAULT_SIGNATURE_MS,
              receiptMs: DEFAULT_RECEIPT_MS,
            },
            {
              onTx: (tx) => set({ txHash: tx }),
            },
          );
        } catch (error: unknown) {
          await orchestrateTx(
            () =>
              approveHandler({
                amount: 0n,
                functionName: 'approve',
                currencyAddress: currency.address,
                userAddress,
                walletClient,
                publicClient,
              }),
            (tx) => publicClient.waitForTransactionReceipt({ hash: tx }),
            {
              signatureMs: DEFAULT_SIGNATURE_MS,
              receiptMs: DEFAULT_RECEIPT_MS,
            },
            {
              onTx: (tx) => set({ txHash: tx }),
            },
          );
          await orchestrateTx(
            () =>
              approveHandler({
                amount: fullAllowance
                  ? fullNumber
                  : BigInt(Math.floor(amount) * 10 ** currency.decimals),
                functionName: 'approve',
                currencyAddress: currency.address,
                userAddress,
                walletClient,
                publicClient,
              }),
            (tx) => publicClient.waitForTransactionReceipt({ hash: tx }),
            {
              signatureMs: DEFAULT_SIGNATURE_MS,
              receiptMs: DEFAULT_RECEIPT_MS,
            },
            {
              onTx: (tx) => set({ txHash: tx }),
            },
          );
        }
      }
    } catch (err: unknown) {
      console.error('Error approving allowance:', err);
      get().handleStoreError(err);
    } finally {
      if (!joinGame && currency && currency.address && currency.decimals != null) {
        await get().fetchCurrentAllowance(userAddress, currency, publicClient);
      }
      set({ isApproving: false, txHash: null });
    }
  },
  startGame: async ({
    wins,
    currency,
    betAmount,
    playerB,
    referrer,
    walletClient,
    publicClient,
    userAddress,
  }) => {
    if (!walletClient || !publicClient) return;
    set({ isStarting: true, error: null });
    try {
      if (isMocked) {
        const mockTxHash = await createRoomMock();
        set({ txHash: mockTxHash });
        await new Promise((res) => setTimeout(res, 500));
        set({ isStarting: false, txHash: mockTxHash });
        return MOCK_ROOM_ID;
      }
      const receipt = await orchestrateTx(
        () =>
          createRoom({
            wins,
            currency,
            betAmount,
            playerB,
            referrer,
            walletClient,
            publicClient,
            userAddress,
          }),
        (tx) => publicClient.waitForTransactionReceipt({ hash: tx }),
        {
          signatureMs: DEFAULT_SIGNATURE_MS,
          receiptMs: DEFAULT_RECEIPT_MS,
        },
        {
          onTx: (tx) => set({ txHash: tx }),
          onTxTimeout: () => {
            Sentry.captureException('Start game transaction timed out');
            console.warn('Start game transaction timed out');
          },

          onReceiptTimeout: () => {
            Sentry.captureException('Start game transaction timed out');
            console.warn('Start game transaction timed out');
          },
          onReceipt: (receipt) => {
            set({ isStarting: false, txHash: null });
          },

          onError: (err) => {
            const typed = err as WriteError | undefined;
            if (typed?.cause?.code) {
              if (typed.cause.code !== 4001) {
                Sentry.captureException(err);
              }
            }
          },
        },
      );
      if (receipt.status !== 'success') {
        console.error('Start game transaction failed:', receipt);
      }
      const logs = (receipt?.logs as unknown as LogWithTopics[]) ?? [];
      const roomCreatedLogs = logs.filter(
        (log) =>
          log?.topics?.[0] === '0x28b80f5ae6f5608b077d8b1643d65d8324aad5b952fec8b9ae8fddb6de866565',
      );
      const hexString = roomCreatedLogs?.[0]?.topics?.[1]?.slice(2) ?? '0';
      const parsed = parseInt(hexString, 16);
      if (receipt.status !== 'success') {
        console.error('Start game transaction failed:', receipt);
      }
      return parsed;
    } catch (err: unknown) {
      console.log('Error starting game:', err);
      get().handleStoreError(err);
    } finally {
      set({ isStarting: false, txHash: null });
    }
  },
  joinRoom: async ({ roomId, referrer, walletClient, publicClient, userAddress }) => {
    if (!walletClient || !publicClient) return;
    set({ isJoining: true, error: null });
    try {
      if (isMocked) {
        set({ txHash: await joinRoomMock() });
        return;
      }
      await orchestrateTx(
        () => joinRoom({ roomId, referrer, walletClient, publicClient, userAddress }),
        (tx) => publicClient.waitForTransactionReceipt({ hash: tx }),
        {
          signatureMs: DEFAULT_SIGNATURE_MS,
          receiptMs: DEFAULT_RECEIPT_MS,
        },
        {
          onTx: (tx) => set({ txHash: tx }),
          onReceipt: (receipt) => {
            if (receipt.status !== 'success') {
              console.error('Join room transaction failed:', receipt);
            }
          },
          onError: (err) => {
            console.error('Error joining room:', err);
            get().handleStoreError(err);
          },
        },
      );
    } catch (err: unknown) {
      console.error('Error joining room:', err);
      get().handleStoreError(err);
      throw err;
    } finally {
      set({ isJoining: false, txHash: null });
    }
  },
}));
